<template>
  <div class="w-full rounded-2xl overflow-hidden bg-black">
    <video :ref="`video-player-${id}`" class="video-js w-full" :poster="metadata.thumbnail"></video>
  </div>
</template>

<script>
// The "!" tells webpack to skip the normal processing for this file
// This prevents Web Workers from breaking due to mangled variable names
import videojs from '!video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-quality-levels';
import '@mycujoo/videojs-hls-quality-selector';
import '@videojs/http-streaming';
import { trackEvent } from '@/utils/mixpanel';
import { trackSermonPlayback } from '@/utils/analytics';

videojs.Vhs.GOAL_BUFFER_LENGTH = 30;
videojs.Vhs.MAX_GOAL_BUFFER_LENGTH = 60 * 60;

export default {
  name: 'VideoPlayer',

  emits: ['play'],

  props: {
    metadata: {
      type: Object,
      required: true,
      validator(metadata) {
        return metadata.url && metadata.thumbnail && metadata.title;
      },
    },
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      player: null,
      lastTimeUpdate: 0,
      accumulatedTimeWatched: 0,
      sendInterval: 30,
      progressTracked: [],
      progressPoints: [0.1, 0.25, 0.5, 0.75, 0.9, 1],
    };
  },

  computed: {
    isHlsStream() {
      const url = this.metadata.url;
      const hlsPatterns = ['.m3u8', 'playlist.m3u8', '/hls/', 'manifest/video.m3u8'];
      return hlsPatterns.some(pattern => url.includes(pattern));
    },

    isIosOrIpad() {
      const isIOSDevice = /iphone|ipad|ipod/i.test(navigator.userAgent);
      const isIPadOS =
        /macintosh/i.test(navigator.userAgent) &&
        'ontouchend' in document &&
        navigator.maxTouchPoints > 0;
      return isIOSDevice || isIPadOS;
    },

    shouldUseNativeHls() {
      return this.isIosOrIpad && this.isHlsStream;
    },

    playerOptions() {
      const controlBarChildren = this.getControlBarChildren();

      return {
        autoplay: false,
        controls: true,
        poster: this.metadata.thumbnail,
        playsinline: true,
        preload: 'auto',
        techOrder: ['html5'],
        sources: [
          {
            src: this.metadata.url,
            type: this.getVideoType(this.metadata.url),
          },
        ],
        fluid: true,
        controlBar: {
          children: controlBarChildren,
        },
        html5: {
          vhs: {
            experimentalBufferBasedABR: true,
            overrideNative: !this.shouldUseNativeHls,
            enableLowInitialPlaylist: true,
            smoothQualityChange: true,
          },
          hls: {
            limitRenditionByPlayerDimensions: true,
            useDevicePixelRatio: true,
            bandwidth: 6194304,
            withCredentials: false,
          },
          nativeVideoTracks: false,
          nativeAudioTracks: false,
          nativeTextTracks: false,
        },
      };
    },
  },

  beforeMount() {
    if (this.metadata?.thumbnail) {
      const img = new Image();
      img.src = this.metadata.thumbnail;
    }
  },

  mounted() {
    this.player = videojs(this.$refs[`video-player-${this.id}`], this.playerOptions, () => {
      this.$nextTick(() => {
        this.setPlaysinlineAttributes();
        // Both of these reach into `player.controlBar` to add a button, so they
        // belong here rather than beside the calls below: `ready` + nextTick is the
        // first point the control bar is guaranteed built. Calling the quality
        // selector straight from mounted() was the actual defect behind the badge
        // bug — the plugin reads `player.controlBar.children()` synchronously, so
        // on a platform where the bar was not up yet it threw, and everything after
        // it in mounted() (including the playback listeners) never ran.
        this.setupQualitySelector();
        // Add custom AirPlay button for native HLS (iOS/iPad)
        if (this.shouldUseNativeHls) {
          this.setupNativeAirPlayButton();
        }
      });
    });

    // Neither of these touches the control bar — listeners attach to the player
    // object, which videojs() has already returned, and mediaSession is a browser
    // API. They stay synchronous so no playback event can outrun them.
    this.addVideoListeners();
    this.setupMediaSession();
  },

  beforeUnmount() {
    if (this.player) {
      this.trackVideoWatchDuration();

      if (this.player.audioTracks) {
        const tracks = this.player.audioTracks();
        tracks.removeEventListener('change', this.trackAudioChange);
      }

      this.player.dispose();
    }
    this.cleanupMediaSession();
  },

  methods: {
    isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },

    getControlBarChildren() {
      const baseControls = [
        'playToggle',
        'volumePanel',
        'currentTimeDisplay',
        'timeDivider',
        'durationDisplay',
        'progressControl',
        'remainingTimeDisplay',
        'subtitlesButton',
        'audioTrackButton',
        'pictureInPictureToggle',
        'fullscreenToggle',
      ];

      if (this.shouldUseNativeHls) {
        return baseControls;
      }

      // No 'qualitySelector' / 'AirPlayToggle' here: neither is a registered
      // video.js component, and `initChildren` silently FILTERS a name it cannot
      // resolve rather than throwing — so both sat in this list doing nothing while
      // reading as configuration. The quality button is really added by the plugin
      // itself (setupQualitySelector), and AirPlay by setupNativeAirPlayButton on
      // the iOS branch above; neither is placed from here.
      return [...baseControls.slice(0, 9), 'pictureInPictureToggle', 'fullscreenToggle'];
    },

    getVideoType(url) {
      if (!url) return null;

      if (this.isHlsUrl(url)) return 'application/x-mpegURL';
      if (url.includes('.mpd')) return 'application/dash+xml';
      if (url.includes('.mp4')) return 'video/mp4';

      if (this.isSafari() || this.isIosOrIpad) {
        console.log('[VideoPlayer] Undetected video type on iOS, defaulting to HLS');
        return 'application/x-mpegURL';
      }

      return null;
    },

    isHlsUrl(url) {
      return url.includes('.m3u8') || url.includes('playlist.m3u8') || url.includes('/hls/');
    },

    setPlaysinlineAttributes() {
      try {
        const tech = this.player.tech();
        const videoElement = tech && tech.el ? tech.el() : null;

        if (videoElement?.tagName === 'VIDEO') {
          videoElement.setAttribute('playsinline', 'true');
          videoElement.setAttribute('webkit-playsinline', 'true');
          videoElement.setAttribute('x5-playsinline', 'true');
          videoElement.setAttribute('x-webkit-airplay', 'allow');
        }
      } catch (error) {
        console.warn('[VideoPlayer] Could not set playsinline attributes:', error);
      }
    },

    // Called from the player's `ready` callback, where the control bar the plugin
    // reaches into exists. The try/catch is a second line only: a quality menu is a
    // nicety, and the plugins behind it also depend on VHS having attached, which
    // differs by platform (iOS plays HLS natively, everything else goes through
    // MSE). A failure here must degrade the control bar and nothing else.
    setupQualitySelector() {
      if (this.shouldUseNativeHls) return;

      try {
        this.player.hlsQualitySelector({
          displayCurrentQuality: true,
        });

        const qualityLevels = this.player.qualityLevels();

        qualityLevels.on('addqualitylevel', event => {
          const qualityLevel = event.qualityLevel;

          if (qualityLevel.height < 720) {
            qualityLevels.removeQualityLevel(qualityLevel);
          } else {
            qualityLevel.enabled = true;
          }
        });
      } catch (error) {
        console.warn('[VideoPlayer] Quality selector unavailable:', error);
      }
    },

    setupNativeAirPlayButton() {
      const Button = videojs.getComponent('Button');
      const player = this.player;

      // Use ES6 class instead of videojs.extend() for video.js 8+ compatibility
      class AirPlayButton extends Button {
        constructor(_player, _options) {
          super(_player, _options);
          this.addClass('vjs-airplay-button');
          this.controlText('AirPlay');

          const iconEl = document.createElement('div');
          iconEl.className = 'vjs-airplay-icon';
          this.el().appendChild(iconEl);

          this.on('click', this.onClick.bind(this));
        }

        onClick() {
          const videoElement = player.el().querySelector('video');
          if (videoElement?.webkitShowPlaybackTargetPicker) {
            console.log('[VideoPlayer] Showing AirPlay picker');
            videoElement.webkitShowPlaybackTargetPicker();
          } else {
            console.warn('[VideoPlayer] AirPlay not supported on this device');
          }
        }
      }

      videojs.registerComponent('AirPlayButton', AirPlayButton);

      const AIRPLAY_BUTTON_POSITION = 10;
      const button = this.player.controlBar.addChild('AirPlayButton', {}, AIRPLAY_BUTTON_POSITION);

      console.log(
        '[VideoPlayer] Native AirPlay button added for iOS/iPad at position:',
        button.el(),
      );
    },

    addVideoListeners() {
      this.player.on('timeupdate', this.trackWatchDuration);
      this.player.on('ended', this.trackVideoEnd);
      this.player.on('play', this.trackVideoStart);
      // Tells the host playback began. The lesson resource card hides its duration
      // badge on this, because it cannot use a DOM click: video.js preventDefaults
      // `touchend` (its `emitTapEvents`) so the browser synthesizes no click at all,
      // which left the badge sitting over the controls on every touch device.
      this.player.on('play', this.emitPlay);
      this.player.on('pause', this.trackVideoPause);
      this.player.on('seeked', this.trackVideoSeek);
      this.player.on('loadeddata', this.updateAudioTrackLabels);
      this.player.on('loadedmetadata', this.onVideoMetadataLoaded);

      if (this.player.audioTracks) {
        const tracks = this.player.audioTracks();
        tracks.addEventListener('change', this.trackAudioChange);
      }
    },

    emitPlay() {
      this.$emit('play');
    },

    trackWatchDuration() {
      const currentTime = this.player.currentTime();
      const totalDuration = this.player.duration();
      const timeWatched = currentTime - this.lastTimeUpdate;

      if (timeWatched < 0) {
        this.lastTimeUpdate = currentTime;
        return;
      }

      this.accumulatedTimeWatched += timeWatched;
      this.trackProgressPoints(currentTime, totalDuration);

      if (this.accumulatedTimeWatched >= this.sendInterval) {
        this.trackVideoWatchDuration();
      }

      this.lastTimeUpdate = currentTime;
      this.updateMediaSessionPositionState();
    },

    trackProgressPoints(currentTime, totalDuration) {
      for (const point of this.progressPoints) {
        if (currentTime >= point * totalDuration && !this.progressTracked.includes(point)) {
          this.trackVideoProgress(point);
          this.progressTracked.push(point);
        }
      }
    },

    trackVideoProgress(progress) {
      const progressPercentage = progress * 100;

      if (window.gtag) {
        window.gtag('event', 'video_progress', {
          video_title: this.metadata.title,
          progress_percentage: progressPercentage,
        });
      }

      trackEvent('Video Progress', {
        video_title: this.metadata.title,
        video_url: this.metadata.url,
        progress_percentage: progressPercentage,
        progress_point: progress,
        video_id: this.id,
      });
      trackSermonPlayback({
        action: 'progressed',
        sermonId: this.id,
        title: this.metadata.title,
        progressPercentage,
        progressPoint: progress,
        source: 'sermon-video-player',
      });
    },

    trackVideoStart() {
      if (this.player.currentTime() > 0) {
        this.lastTimeUpdate = this.player.currentTime();
        this.updateMediaSessionMetadata();
        return;
      }

      this.progressTracked = [];
      this.accumulatedTimeWatched = 0;
      this.lastTimeUpdate = 0;

      if (window.gtag) {
        window.gtag('event', 'video_start', {
          video_title: this.metadata.title,
        });
      }

      trackEvent('Video Start', {
        video_title: this.metadata.title,
        video_url: this.metadata.url,
        video_id: this.id,
        video_duration: this.player.duration(),
      });
      trackSermonPlayback({
        action: 'started',
        sermonId: this.id,
        title: this.metadata.title,
        duration: this.player.duration(),
        source: 'sermon-video-player',
      });

      this.updateMediaSessionMetadata();
    },

    trackVideoEnd() {
      this.trackVideoWatchDuration();

      if (window.gtag) {
        window.gtag('event', 'video_end', {
          video_title: this.metadata.title,
        });
      }

      trackEvent('Video End', {
        video_title: this.metadata.title,
        video_url: this.metadata.url,
        video_id: this.id,
        video_duration: this.player.duration(),
        total_time_watched: Math.ceil(this.accumulatedTimeWatched),
      });
      trackSermonPlayback({
        action: 'completed',
        sermonId: this.id,
        title: this.metadata.title,
        duration: this.player.duration(),
        timeWatched: Math.ceil(this.accumulatedTimeWatched),
        source: 'sermon-video-player',
      });
    },

    trackVideoWatchDuration() {
      const MIN_TIME_TO_TRACK = 5;

      if (this.accumulatedTimeWatched < MIN_TIME_TO_TRACK) {
        this.accumulatedTimeWatched = 0;
        return;
      }

      if (window.gtag) {
        window.gtag('event', 'video_watch_duration', {
          video_title: this.metadata.title,
          video_id: this.id,
          time_watched: Math.ceil(this.accumulatedTimeWatched),
        });
      }

      trackEvent('Video Watch Duration', {
        video_title: this.metadata.title,
        video_url: this.metadata.url,
        video_id: this.id,
        time_watched: Math.ceil(this.accumulatedTimeWatched),
      });

      this.accumulatedTimeWatched = 0;
    },

    trackVideoPause() {
      this.trackVideoWatchDuration();
      this.updateMediaSessionPositionState();
    },

    trackVideoSeek() {
      const currentTime = this.player.currentTime();
      this.trackVideoWatchDuration();
      this.lastTimeUpdate = currentTime;
    },

    trackAudioChange() {
      try {
        const tracks = this.player.audioTracks();
        if (!tracks) return;

        // Handle both iterable (video.js 7) and non-iterable (video.js 8+) TrackList
        const tracksArray = tracks.tracks ? tracks.tracks : Array.from(tracks);
        const activeTracks = tracksArray.filter(track => track.enabled);

        if (activeTracks.length === 0 || !window.gtag) return;

        window.gtag('event', 'audio_track_change', {
          video_title: this.metadata.title,
          audio_track: activeTracks[0].label,
        });
      } catch (e) {
        console.warn('[VideoPlayer] Could not track audio change:', e);
      }
    },

    updateAudioTrackLabels() {
      try {
        const audioTrackList = this.player.audioTracks();
        if (!audioTrackList) return;

        // video.js 8+ - access tracks through the tracks array
        const tracks = audioTrackList.tracks || Array.from(audioTrackList);
        for (const track of tracks) {
          if (track.label === 'original') {
            track.label = 'English';
          }
        }
      } catch (e) {
        console.warn('[VideoPlayer] Could not update audio track labels:', e);
      }
    },

    onVideoMetadataLoaded() {
      this.updateMediaSessionPositionState();
    },

    setupMediaSession() {
      if (!('mediaSession' in navigator)) return;

      const DEFAULT_SEEK_OFFSET = 10;

      navigator.mediaSession.setActionHandler('play', () => {
        this.player?.paused() && this.player.play();
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        !this.player?.paused() && this.player.pause();
      });

      navigator.mediaSession.setActionHandler('seekto', details => {
        if (this.player && details.seekTime !== undefined) {
          this.player.currentTime(details.seekTime);
        }
      });

      navigator.mediaSession.setActionHandler('seekbackward', details => {
        if (!this.player) return;
        const seekTime = Math.max(
          0,
          this.player.currentTime() - (details.seekOffset || DEFAULT_SEEK_OFFSET),
        );
        this.player.currentTime(seekTime);
      });

      navigator.mediaSession.setActionHandler('seekforward', details => {
        if (!this.player) return;
        const duration = this.player.duration();
        const seekTime = Math.min(
          duration,
          this.player.currentTime() + (details.seekOffset || DEFAULT_SEEK_OFFSET),
        );
        this.player.currentTime(seekTime);
      });
    },

    async updateMediaSessionMetadata() {
      if (!('mediaSession' in navigator) || !this.player) return;

      const artwork = await this.loadArtwork();
      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.metadata.title,
        artist: 'HOGC',
        artwork,
      });
    },

    async loadArtwork() {
      if (!this.metadata.thumbnail) return [];

      const thumbnail = this.metadata.thumbnail;
      const ARTWORK_SIZES = ['512x512', '256x256', '128x128'];

      return ARTWORK_SIZES.map(size => ({
        src: thumbnail,
        sizes: size,
        type: 'image/jpeg',
      }));
    },

    updateMediaSessionPositionState() {
      if (!('mediaSession' in navigator) || !this.player) return;

      const duration = this.player.duration();
      const currentTime = this.player.currentTime();

      if (Number.isNaN(duration) || Number.isNaN(currentTime)) return;

      navigator.mediaSession.setPositionState({
        duration,
        playbackRate: this.player.playbackRate(),
        position: currentTime,
      });
    },

    pause() {
      this.player?.pause();
    },

    cleanupMediaSession() {
      if (!('mediaSession' in navigator)) return;

      navigator.mediaSession.metadata = null;

      const MEDIA_SESSION_HANDLERS = ['play', 'pause', 'seekto', 'seekbackward', 'seekforward'];

      MEDIA_SESSION_HANDLERS.forEach(handler => {
        try {
          navigator.mediaSession.setActionHandler(handler, null);
        } catch (error) {
          console.warn(`Failed to remove Media Session handler: ${handler}`, error);
        }
      });
    },
  },
};
</script>

<style>
.video-js .vjs-time-control {
  display: block;
  padding-left: 0 !important;
  min-width: 1em !important;
}

#vjs_video_3 > div.vjs-control-bar > div.vjs-time-control.vjs-time-divider {
  width: auto;
  padding-right: 0 !important;
}

#vjs_video_3 > div.vjs-control-bar > div.vjs-duration.vjs-time-control.vjs-control {
  padding-right: 0 !important;
}

.video-js .vjs-remaining-time {
  display: none;
}

.video-js .vjs-menu li {
  text-transform: initial;
}

.video-js .vjs-big-play-button {
  content: '';
  background-repeat: no-repeat;
  background-size: 52px;
  background-position: 50% calc(50% - 0px);
  border: none !important;
  box-shadow: none !important;
  filter: invert(5%) sepia(3%) saturate(2850%) hue-rotate(184deg) brightness(98%) contrast(99%);
}

.video-js .vjs-control-bar {
  z-index: 10 !important;
  pointer-events: auto !important;
  position: absolute !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  top: auto !important;
}

.video-js .vjs-tech {
  pointer-events: auto;
}

.video-js.vjs-user-active .vjs-control-bar {
  z-index: 10 !important;
  pointer-events: auto !important;
}

@supports (-webkit-touch-callout: none) {
  .video-js .vjs-control-bar {
    z-index: 10 !important;
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
  }
}

/* Custom AirPlay button styling */
.video-js .vjs-airplay-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  width: 2.8em;
  height: 1.8em;
}

.video-js .vjs-airplay-button:before {
  content: '';
  position: relative;
  display: block;
  width: 1.2em;
  height: 1.2em;
}

/* AirPlay icon - using CSS shapes */
.video-js .vjs-airplay-icon {
  position: relative;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-js .vjs-airplay-icon:before {
  content: '';
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 14px;
  height: 10px;
  background: currentColor;
  border-radius: 2px;
}

.video-js .vjs-airplay-icon:after {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 8px solid currentColor;
}

.video-js .vjs-airplay-button:hover {
  color: #fff;
}

.video-js .vjs-airplay-button:focus {
  text-shadow:
    0 0 1em #fff,
    0 0 1em #fff,
    0 0 1em #fff;
}

.video-js .vjs-airplay-button:hover .vjs-airplay-icon:before,
.video-js .vjs-airplay-button:hover .vjs-airplay-icon:after {
  opacity: 1;
}
</style>
